import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { eq, isEmpty, includes } from 'lodash';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import {
  loadAuthorContent,
  loadOutletContent,
  addAuthorToProject,
  deleteAuthorToProject,
  addOutletToProject,
  deleteOutletToProject,
  showNotification,
  initAuthorOutletToProjectState,
  setTopicDialog,
  deleteTopicToProject,
  addTopicToProject,
} from 'redux/actions';
import { FacetSearch, Notification, Search } from 'app/models';
import {
  RECENT_SORT,
  STAR_ICON,
  STAR_ICON_FILL,
  SPINNER_ICON,
} from 'app/constants';
import { FacetContent } from '../dashboard-facets/facet-content.interface';

enum EntityTitle {
  Authors = 'Authors',
  Outlets = 'Outlets',
  Topics = 'Topics',
  Companies = 'Companies',
}

@Component({
  selector: 'app-content-table',
  template: `
    <div class="facet-panel" *ngIf="entity" [class.facet-panel--tags]="displayMode === 'tags'">
      <ng-container *ngIf="displayMode === 'tags'; else peopleTable">
        <div class="facet-tag-list" *ngIf="tableItems.length; else noItems">
          <div class="facet-tag" *ngFor="let facet of tableItems">
            <button *ngIf="facet.starIcon" class="facet-tag__save" type="button" [attr.aria-label]="'Save ' + facet.name" (click)="handleSaveFacetItemToProject(facet, entity.title)"><i [class]="facet.starIcon"></i></button>
            <button class="facet-tag__name" type="button" [title]="facet.name" (click)="showModalContent(entity.title, facet)">{{ facet.name }}</button>
          </div>
        </div>
      </ng-container>
      <ng-template #peopleTable>
        <p-table [value]="tableItems" styleClass="p-datatable-sm" [scrollable]="true" scrollHeight="flex" sortField="numItems" [sortOrder]="-1">
          <ng-template pTemplate="header">
            <tr>
              <th [style.width.%]="columnWidths.name" pSortableColumn="name">{{ entity.title === EntityTitle.Authors ? 'Author' : 'Outlet' }} <p-sortIcon field="name"></p-sortIcon></th>
              <th [style.width.%]="columnWidths.geo" *ngIf="entity.title === EntityTitle.Outlets" pSortableColumn="influenceScore">GEO Score <i class="pi pi-question-circle" pTooltip="Measures how likely an outlet’s coverage is to influence AI-generated answers, blending independently calibrated ChatGPT, Gemini, and Claude evaluations of authority, accessibility, retrievability, and persistence." (click)="$event.stopPropagation()"></i> <p-sortIcon field="influenceScore"></p-sortIcon></th>
              <th [style.width.%]="columnWidths.articles" pSortableColumn="numItems"># of Articles <p-sortIcon field="numItems"></p-sortIcon></th>
              <th [style.width.%]="columnWidths.recent" pSortableColumn="maxDate">Most Recent <p-sortIcon field="maxDate"></p-sortIcon></th>
              <th [style.width.%]="columnWidths.coverage" *ngIf="showMyCompany" pSortableColumn="myCompanyItems">My Coverage <i class="pi pi-question-circle" pTooltip="Number of articles that mentioned My Company"></i> <p-sortIcon field="myCompanyItems"></p-sortIcon></th>
              <th [style.width.%]="columnWidths.coverageRecent" *ngIf="showMyCompany" pSortableColumn="myCompanyMaxDate">Most Recent <i class="pi pi-question-circle" pTooltip="Most recent article that mentioned My Company"></i> <p-sortIcon field="myCompanyMaxDate"></p-sortIcon></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-facet>
            <tr>
              <td [style.width.%]="columnWidths.name"><div class="facet-person"><button *ngIf="facet.starIcon" class="facet-person__star" type="button" [attr.aria-label]="'Save ' + facet.name" (click)="handleSaveFacetItemToProject(facet, entity.title)"><i [class]="facet.starIcon"></i></button><img *ngIf="facet.imageUrl || facet.picture" [src]="facet.imageUrl || facet.picture" alt="" class="facet-person__avatar"><span *ngIf="!facet.imageUrl && !facet.picture" class="facet-person__avatar facet-person__avatar--empty"><i class="pi pi-user"></i></span><span class="facet-person__details"><button type="button" class="facet-person__name" (click)="showModalContent(entity.title, facet)">{{ facet.name }}</button><span *ngIf="entity.title === EntityTitle.Authors && facet.outletNames" class="facet-person__outlets" [title]="facet.outletNames">{{ facet.outletNames }}</span></span></div></td>
              <td [style.width.%]="columnWidths.geo" *ngIf="entity.title === EntityTitle.Outlets"
                  pTooltip="Measures how likely an outlet’s coverage is to influence AI-generated answers, blending independently calibrated ChatGPT, Gemini, and Claude evaluations of authority, accessibility, retrievability, and persistence."
              >{{ facet.influenceScore != null ? facet.influenceScore + '%' : '—' }}</td>
              <td [style.width.%]="columnWidths.articles"><span>{{ facet.numItems | number }}</span><div class="facet-articles-track" role="img" [attr.aria-label]="(facet.numItems | number) + ' articles'"> <span class="facet-articles-fill" [style.width.%]="articleProgress(facet)"></span></div></td>
              <td [style.width.%]="columnWidths.recent">{{ facet.maxDate ? (facet.maxDate | date: 'MMM dd, yyyy') : '—' }}</td>
              <td [style.width.%]="columnWidths.coverage" *ngIf="showMyCompany">{{ facet.myCompanyItems != null ? (facet.myCompanyItems | number) : '—' }}</td>
              <td [style.width.%]="columnWidths.coverageRecent" *ngIf="showMyCompany">{{ facet.myCompanyMaxDate ? (facet.myCompanyMaxDate | date: 'MMM dd, yyyy') : '—' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage"><tr><td [attr.colspan]="showMyCompany ? (entity.title === EntityTitle.Authors ? 5 : 6) : (entity.title === EntityTitle.Authors ? 3 : 4)">No records found</td></tr></ng-template>
        </p-table>
      </ng-template>
      <ng-template #noItems><p class="facet-panel__empty">No {{ entity.title | lowercase }} found.</p></ng-template>
    </div>
  `,
  styleUrls: ['./facets-table.scss'],
})
export class ContentTableComponent implements OnInit, OnChanges {
  @Input() entity: FacetContent;
  @Input() displayMode: 'people' | 'tags' = 'people';
  @Output() viewedEntity = new EventEmitter<'Authors' | 'Outlets'>();
  @Input() isContactProfile: boolean;
  @Input() authorsSavedList: any[];
  @Input() outletsSavedList: any[];
  @Input() topicsSavedList: any[];
  public keywords: string;
  public currentProject: any;
  public outletsList: any[];
  public authorsList: any[];
  public topicsList: any[];
  public starSelected: string;
  public starUnSelected: string;
  public tableItems: any[];
  public maxArticleCount = 0;
  public columnWidths = { name: 50, geo: 0, articles: 25, recent: 25, coverage: 0, coverageRecent: 0 };
  public project: any;
  public authorOutletOperation: any;
  public sort: any;
  public screenSize: number;
  public sliceControl: number;
  public visibleEllipses: boolean;
  public isInitList: boolean;
  public showNumberItem: boolean;
  public authors: number[];
  public outlets: number[];
  public searchBody: Search;
  public readonly EntityTitle = EntityTitle;

  constructor(private store: Store<AppState>) {
    this.keywords = '';
    this.starUnSelected = STAR_ICON;
    this.starSelected = STAR_ICON_FILL;
    this.tableItems = [];
    this.authorsList = [];
    this.outletsList = [];
    this.authors = [];
    this.outlets = [];
    this.project = null;
    this.authorOutletOperation = {
      typeOperation: null,
      entity: null,
      itemIndex: null,
    };
    this.visibleEllipses = true;
    this.sliceControl = 9;
    this.isInitList = true;
    this.showNumberItem = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.authorsSavedList) { this.authorsList = this.authorsSavedList || []; }
    if (changes.outletsSavedList) { this.outletsList = this.outletsSavedList || []; }
    if (changes.topicsSavedList) { this.topicsList = this.topicsSavedList || []; }
    if (!this.entity || !this.entity.contents) { return; }

    this.tableItems = this.entity.contents.map(item => {
      const saved = this.entity.title === EntityTitle.Authors
        ? this.authorsList.find(entry => eq(entry.partyId, item.id))
        : this.entity.title === EntityTitle.Outlets
          ? this.outletsList.find(entry => eq(entry.partyId, item.id))
          : this.topicsList.find(entry => eq(entry.id, item.id));
      return {
        ...item,
        myCompanyItems: item.myCompanyItems != null ? item.myCompanyItems : item.myCompanyStats?.numItems,
        myCompanyMaxDate: item.myCompanyMaxDate || item.myCompanyStats?.maxDate,
        company: this.entity.title === EntityTitle.Companies,
        userTopicId: saved?.userTopicId,
        starIcon: saved ? this.starSelected : this.starUnSelected,
      };
    });
    this.maxArticleCount = Math.max(0, ...this.tableItems.map(item => Number(item.numItems) || 0));
    const withCoverage = this.showMyCompany;
    this.columnWidths = this.entity.title === EntityTitle.Authors
      ? withCoverage
        ? { name: 29, geo: 0, articles: 19, recent: 16, coverage: 18, coverageRecent: 18 }
        : { name: 50, geo: 0, articles: 25, recent: 25, coverage: 0, coverageRecent: 0 }
      : withCoverage
        ? { name: 27, geo: 12, articles: 16, recent: 15, coverage: 14, coverageRecent: 16 }
        : { name: 45, geo: 15, articles: 22, recent: 18, coverage: 0, coverageRecent: 0 };
  }

  ngOnInit() {
    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.project = projectSelected;
      }
    });

    this.store.select('authorsOutletsList').subscribe(({ errors }) => {
      const { typeOperation, entity } = this.authorOutletOperation;
      if (errors && typeOperation) {
        this.store.dispatch(
          showNotification({
            notify: new Notification(
              `Error ${typeOperation} ${entity} to project`,
              'error',
              'tl'
            ),
          })
        );
        this.authorOutletOperation = {
          typeOperation: null,
          entity: null,
          tableItem: null,
        };
        setTimeout(() => {
          this.store.dispatch(initAuthorOutletToProjectState());
        }, 500);
      }
    });

    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        this.authors = searchBody.authors;
        this.outlets = searchBody.outlets;
        this.keywords = searchBody.keywords;
        this.searchBody = searchBody;
      }
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.currentProject = projectSelected;
      }
    });

    this.store.select('ui').subscribe(({ screenSizeValues }) => {
      this.screenSize = screenSizeValues.scrWidth;
      if (this.screenSize <= 1700) {
        this.sliceControl = 18;
        this.visibleEllipses = false;
        if (this.screenSize < 1500) {
          this.sliceControl = 9;
        }
      } else {
        this.visibleEllipses = true;
      }
      if (this.screenSize >= 1500) {
        this.showNumberItem = true;
      } else {
        this.showNumberItem = false;
      }
    });
  }

  handleShowSpinnerIcon(id) {
    const index = this.tableItems.findIndex((t) => t.id === id);
    const preIcon = this.tableItems[index].starIcon;
    this.tableItems[index].starIcon = SPINNER_ICON;
    return preIcon;
  }

  handleHideSpinnerIcon(id, preIcon) {
    const index = this.tableItems.findIndex((t) => t.id === id);
    setTimeout(() => {
      this.tableItems[index] = { ...this.tableItems[index], starIcon: preIcon };
    }, 3000);
  }

  showModalContent(entity, item) {
    const preIcon = this.handleShowSpinnerIcon(item.id);
    if (eq(entity, EntityTitle.Authors)) {
      this.getAuthorContent(item);
      this.viewedEntity.emit('Authors');
    }

    if (eq(entity, EntityTitle.Outlets)) {
      this.getOutletContent(item);
      this.viewedEntity.emit('Outlets');
    }

    if (eq(entity, EntityTitle.Topics)) {
      this.getTopicContent(item);
    }

    if (eq(entity, EntityTitle.Companies)) {
      this.getTopicContent(item);
    }
    this.handleHideSpinnerIcon(item.id, preIcon);
  }

  /**
   * Handle star icon action
   */
  handleSaveFacetItemToProject(valueItem, entity) {
    // Author update
    if (eq(entity, EntityTitle.Authors)) {
      const itemIndex = this.tableItems.findIndex((item) =>
        eq(item.id, valueItem.id)
      );
      const isSaved = eq(
        this.tableItems[itemIndex].starIcon,
        this.starSelected
      );

      const author = {
        ...this.tableItems[itemIndex],
        starIcon: isSaved ? STAR_ICON : STAR_ICON_FILL,
      };
      this.tableItems[itemIndex] = author; // final item update

      if (isSaved) {
        this.authorOutletOperation = {
          typeOperation: 'deleting',
          entity: 'author',
          itemIndex: itemIndex,
        };
        this.store.dispatch(
          deleteAuthorToProject({
            partyId: valueItem.id,
            userProject: this.project,
            author: valueItem,
          })
        );
      } else {
        this.authorOutletOperation = {
          typeOperation: 'adding',
          entity: 'author',
          itemIndex: itemIndex,
        };
        this.store.dispatch(
          addAuthorToProject({
            partyId: valueItem.id,
            userProject: this.project,
            author: valueItem,
          })
        );
      }
    }

    // Outlet update
    if (eq(entity, EntityTitle.Outlets)) {
      const itemIndex = this.tableItems.findIndex((item) =>
        eq(item.id, valueItem.id)
      );
      const isSaved = eq(
        this.tableItems[itemIndex].starIcon,
        this.starSelected
      );

      const outlet = {
        ...this.tableItems[itemIndex],
        starIcon: isSaved ? STAR_ICON : STAR_ICON_FILL,
      };
      this.tableItems[itemIndex] = outlet; // final item update

      if (isSaved) {
        this.authorOutletOperation = {
          typeOperation: 'deleting',
          entity: 'outlet',
          itemIndex: itemIndex,
        };
        this.store.dispatch(
          deleteOutletToProject({
            partyId: valueItem.id,
            userProject: this.project,
            outlet: valueItem,
          })
        );
      } else {
        this.authorOutletOperation = {
          typeOperation: 'adding',
          entity: 'outlet',
          itemIndex: itemIndex,
        };
        this.store.dispatch(
          addOutletToProject({
            partyId: valueItem.id,
            userProject: this.project,
            outlet: valueItem,
          })
        );
      }
    }

    // Topics update
    if (eq(entity, EntityTitle.Topics) || eq(entity, EntityTitle.Companies)) {
      const itemIndex = this.tableItems.findIndex((item) =>
        eq(item.id, valueItem.id)
      );
      const isSaved = eq(
        this.tableItems[itemIndex].starIcon,
        this.starSelected
      );

      const topic = {
        ...this.tableItems[itemIndex],
        starIcon: isSaved ? STAR_ICON : STAR_ICON_FILL,
      };
      this.tableItems[itemIndex] = topic; // final item update

      if (isSaved) {
        this.store.dispatch(
          deleteTopicToProject({
            topic,
            userProject: this.project,
          })
        );
      } else {
        this.store.dispatch(
          addTopicToProject({
            topic,
            userProject: this.project,
          })
        );
      }
    }
  }

  getAuthorContent(author) {
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
        authors: [author.id],
        outlets: this.outlets,
      }),
    });

    this.store.dispatch(
      loadAuthorContent({
        facetSearch,
        author,
      })
    );
  }

  getOutletContent(outlet) {
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
        outlets: [outlet.id],
        authors: this.authors,
      }),
    });

    this.store.dispatch(
      loadOutletContent({
        facetSearch,
        outlet,
      })
    );
  }

  getTopicContent(topic) {
    const sort = /* this.keywords ? RELEVANT_SORT : */ RECENT_SORT;
    this.store.dispatch(
      setTopicDialog({
        topicSelected: topic,
        projectSelected: this.currentProject,
        keywords: this.keywords,
        sort,
        isSidebarTopic: false,
        authors: this.authors,
        outlets: this.outlets,
      })
    );
  }

  getYahooFinanceURL(value: any) {
    if (isEmpty(value)) {
      return null;
    } else if (includes(value, 'http')) {
      const q = value.substring(value.lastIndexOf('/') + 1, value.length);
      return `https://finance.yahoo.com/quote/${q}`;
    } else {
      return `https://finance.yahoo.com/quote/${value}`;
    }
  }

  get showMyCompany(): boolean {
    return this.tableItems.some(item => item.myCompanyItems != null || item.myCompanyMaxDate != null);
  }

  get tagItems(): any[] {
    return [...this.tableItems]
      .sort((a, b) => this.tagScore(b) - this.tagScore(a))
      .slice(0, 15);
  }

  private tagScore(facet: any): number {
    return Number(facet.percentage != null ? facet.percentage : facet.score) || 0;
  }

  articleProgress(facet: any): number {
    return this.maxArticleCount ? Math.max(0, Math.min(100, (Number(facet.numItems) || 0) * 100 / this.maxArticleCount)) : 0;
  }

  getFirstOutletName(outletNames: string): string {
    if (!outletNames) {
      return '';
    }
    const names = outletNames.split(',').map((name) => name.trim());
    return names[0] || '';
  }
}
